import { Text } from "@/design-system";
import { t } from "@/context/language";
import { cn } from "@/utils";
import { ChevronDownIcon } from "@heroicons/react/24/outline";
import { motion } from "framer-motion";
import { useEffect } from "react";

export type FAQItem = { question: string; answer: string };

type FAQProps = {
  // Perguntas específicas da página. Quando informado, substitui o conjunto
  // genérico institucional (t.faq.items) — tanto na renderização quanto no
  // JSON-LD — para evitar FAQPage duplicado entre páginas diferentes.
  items?: FAQItem[];
  headingOverride?: string;
  /** Classes extras do título — ex.: LP no Design System usa a escala `text-ds-h2`. */
  headingClassName?: string;
};

// FAQPage JSON-LD — sempre em PT-BR (idioma canônico indexado), independente
// do idioma exibido na tela. Renderizado inline (no body) porque o Helmet não
// está aplicando head nesta LP. Sai do MESMO array que a tela renderiza: o
// Google exige que o texto do schema esteja visível na página.
function buildFaqPageSchema(items: FAQItem[]) {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    inLanguage: "pt-BR",
    mainEntity: items.map(item => ({
      "@type": "Question",
      name: item.question,
      acceptedAnswer: {
        "@type": "Answer",
        text: item.answer,
      },
    })),
  };
}

// Âncora estável por pergunta (`/#faq-quanto-custa-a-fidexa`): permite linkar
// direto para uma resposta — do blog, do suporte, de um anúncio.
const anchorFor = (question: string) =>
  "faq-" +
  question
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "");

export function FAQ({
  items,
  headingOverride,
  headingClassName,
}: FAQProps = {}) {
  const faqItems = items ?? t.faq.items;
  const faqPageSchema = buildFaqPageSchema(faqItems);

  // Quem chega por uma âncora encontra a resposta já aberta — ao carregar a
  // página ou ao clicar num link para outra pergunta da mesma página.
  useEffect(() => {
    const openFromHash = () => {
      const id = decodeURIComponent(window.location.hash.slice(1));
      if (!id.startsWith("faq-")) return;
      const target = document.getElementById(id);
      if (target instanceof HTMLDetailsElement) {
        target.open = true;
        target.scrollIntoView({ block: "center" });
      }
    };
    openFromHash();
    window.addEventListener("hashchange", openFromHash);
    return () => window.removeEventListener("hashchange", openFromHash);
  }, []);

  return (
    <div>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(faqPageSchema) }}
      />
      <h2
        className={cn(
          "font-ds-display text-ds-ink-900 text-ds-h1 sm:text-ds-display mb-10 text-center font-bold lg:mb-14",
          headingClassName
        )}
      >
        {headingOverride ?? t.faq.heading}
      </h2>

      {/* `<details>` nativo em vez de acordeão montado em JS: a resposta
          fica no HTML prerenderizado (o crawler lê sem clicar), abre com
          teclado sem código extra e a busca do navegador (Ctrl+F) acha texto
          de resposta fechada. Mesmo padrão do bloco FAQ do blog. */}
      <div className="grid grid-cols-1 items-start gap-3 sm:gap-4 lg:grid-cols-2 lg:gap-6">
        {faqItems.map((item, index) => (
          <motion.details
            key={item.question}
            id={anchorFor(item.question)}
            name="faq"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: (index % 2) * 0.1 }}
            className="group rounded-ds-xl bg-ds-surface-sunken h-fit scroll-mt-28 overflow-hidden"
          >
            <summary className="focus-visible:outline-ds-focus rounded-ds-xl flex cursor-pointer list-none items-center justify-between gap-4 p-5 text-left focus-visible:outline-2 focus-visible:-outline-offset-2 sm:p-6 [&::-webkit-details-marker]:hidden">
              <Text as="h3" size="lead" weight="medium">
                {item.question}
              </Text>
              <span
                aria-hidden="true"
                className="bg-ds-brand-100 ease-ds-out flex size-10 shrink-0 items-center justify-center rounded-full transition-transform duration-200 group-open:rotate-180 motion-reduce:transition-none"
              >
                <ChevronDownIcon className="text-ds-brand-600 size-5" />
              </span>
            </summary>

            <div className="px-5 pb-5 sm:px-6 sm:pb-6">
              <Text tone="muted" className="leading-relaxed">
                {item.answer}
              </Text>
            </div>
          </motion.details>
        ))}
      </div>
    </div>
  );
}
